import { useState, useMemo, useCallback } from 'react';
import {
  Calendar as CalendarIcon,
  Repeat,
  Loader2,
} from 'lucide-react';
import { Program, Track } from '../data/mockData';
import { API_BASE_URL, useListTracksQuery, useListProgramsPaginatedQuery } from '../../store/api';
import { format, isAfter } from 'date-fns';
import { parseApiDate } from '../utils/dateTime';
import { ProgramDetailPage } from './ProgramDetailPage';
import { useServerInfiniteScroll } from '../hooks/useServerInfiniteScroll';

const PAGE_SIZE = 20;

interface EventsTabProps {
  channelId: string | number;
}

export function EventsTab({ channelId }: EventsTabProps) {
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [pageOffset, setPageOffset] = useState(0);

  const { data: paginatedData, isFetching } = useListProgramsPaginatedQuery({
    channelId: Number(channelId),
    offset: pageOffset,
    limit: PAGE_SIZE,
  });

  const rawPrograms: Program[] = useMemo(() => {
    return ((paginatedData as any)?.items ?? []).map((p: any) => {
      const rawTrackId = p.trackId ?? p.track_id;
      return {
        ...p,
        id: String(p.id),
        channelId: String(p.channelId ?? p.channel_id),
        trackId: rawTrackId != null ? String(rawTrackId) : null,
      };
    });
  }, [paginatedData]);

  const hasMore = (paginatedData as any)?.hasMore ?? false;

  const { sentinelRef } = useServerInfiniteScroll({
    hasMore,
    isFetching,
    onLoadMore: useCallback(() => setPageOffset((prev) => prev + PAGE_SIZE), []),
  });

  // Lazy-load tracks only when detail view is open
  const { data: tracksData = [] } = useListTracksQuery(
    selectedProgram ? Number(channelId) : (undefined as any),
    { skip: !selectedProgram }
  );
  const tracks: Track[] = (tracksData as any[]).map((t: any) => ({
    ...t,
    id: String(t.id),
    channelId: String(t.channelId ?? t.channel_id),
    fileName: t.fileName ?? t.file_name,
    uploadDate: t.uploadDate ?? t.upload_date,
  }));

  const sortedPrograms = useMemo(() => {
    return [...rawPrograms].sort((a, b) => {
      const dateA = parseApiDate(a.startDateTime).getTime();
      const dateB = parseApiDate(b.startDateTime).getTime();
      return dateA - dateB;
    });
  }, [rawPrograms]);

  const upcomingPrograms = useMemo(() => {
    const now = new Date();
    return sortedPrograms.filter((p) => isAfter(parseApiDate(p.startDateTime), now));
  }, [sortedPrograms]);

  const formatDt = (v: Date | string) => {
    try {
      const d = parseApiDate(v);
      return { date: format(d, 'EEE MMM d'), time: format(d, 'h:mm a') };
    } catch {
      return { date: 'Invalid date', time: '' };
    }
  };

  const truncate = (text: string, max: number) =>
    text.length <= max ? text : text.slice(0, max).trimEnd() + '…';

  if (selectedProgram) {
    return (
      <ProgramDetailPage
        program={selectedProgram}
        tracks={tracks}
        channelId={channelId}
        onBack={() => setSelectedProgram(null)}
      />
    );
  }

  if (rawPrograms.length === 0 && isFetching) {
    return (
      <div className="p-6 sm:p-8 flex items-center justify-center py-24">
        <Loader2 className="size-8 animate-spin text-gray-500" />
      </div>
    );
  }

  if (rawPrograms.length === 0) {
    return (
      <div className="p-6 sm:p-8">
        <h2 className="text-2xl font-bold text-white mb-2">Events</h2>
        <p className="text-gray-400 mb-8">Upcoming and past program events</p>
        <div className="text-center py-16 bg-gray-800 rounded-lg border border-gray-700">
          <CalendarIcon className="size-12 mx-auto mb-4 text-gray-600" />
          <p className="text-gray-400 text-lg">No events yet</p>
          <p className="text-gray-500 text-sm mt-2">
            Events will appear here once programs are created
          </p>
        </div>
      </div>
    );
  }

  /* Featured event = first in sorted list; remaining go to the left column below */
  const [featured, ...restPrograms] = sortedPrograms;
  const featStart = formatDt(featured.startDateTime);
  const featEnd = formatDt(featured.endDateTime);
  const featThumb = featured.thumbnail
    ? featured.thumbnail.startsWith('/') ? `${API_BASE_URL}${featured.thumbnail}` : featured.thumbnail
    : null;

  return (
    <div className="p-6 sm:p-8">
      {/* Two-column editorial layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">

        {/* ── LEFT: Featured + All Events (≈60%) ── */}
        <div className="lg:col-span-3 max-w-xl mx-auto">
          {/* Featured event card */}
          <article className="mb-10 group">
            <h2 className="text-2xl font-bold text-white leading-tight mb-4">
              {featured.title}
            </h2>

            {/* Large poster image */}
            {featThumb && (
              <div
                className="w-full rounded-lg overflow-hidden mb-5 cursor-pointer"
                onClick={() => setSelectedProgram(featured)}
              >
                <img
                  src={featThumb}
                  alt={featured.title}
                  loading="lazy"
                  className="w-full max-h-[320px] object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                />
              </div>
            )}

            {/* Date / time */}
            <p className="text-sm font-bold text-gray-200 mb-1">
              {featStart.date}, {featStart.time}
              {featEnd.time && <span className="font-normal text-gray-400"> – {featEnd.time}</span>}
            </p>

            {/* Recurring badge */}
            {featured.scheduleType === 'recurring' && (
              <p className="text-xs text-purple-400 italic mb-2 flex items-center gap-1">
                <Repeat className="size-3" />
                {featured.recurrence?.frequency === 'daily' ? 'Daily' : 'Weekly'}
                {featured.recurrence?.frequency === 'weekly' && featured.recurrence.daysOfWeek && featured.recurrence.daysOfWeek.length > 0 &&
                  ` · ${featured.recurrence.daysOfWeek.slice().sort((a, b) => a - b).map(d => ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d]).join(', ')}`}
              </p>
            )}

            {/* Description */}
            {featured.description && (
              <p className="text-sm text-gray-300 leading-relaxed mt-2 whitespace-pre-line">
                {featured.description}
              </p>
            )}

            <button
              onClick={() => setSelectedProgram(featured)}
              className="mt-3 text-sm text-blue-400 hover:text-blue-300 font-semibold transition-colors"
            >
              …Detail »
            </button>
          </article>

          {/* Remaining events below featured */}
          {restPrograms.length > 0 && (
            <>
              <div className="border-t border-gray-700 mb-6" />
              <div className="space-y-6">
                {restPrograms.map((program) => {
                  const s = formatDt(program.startDateTime);
                  const e = formatDt(program.endDateTime);
                  const thumb = program.thumbnail
                    ? program.thumbnail.startsWith('/') ? `${API_BASE_URL}${program.thumbnail}` : program.thumbnail
                    : null;

                  return (
                    <article key={program.id} className="group">
                      <h3 className="text-xl font-bold text-white leading-tight mb-3">
                        {program.title}
                      </h3>

                      {thumb && (
                        <div
                          className="w-full rounded-lg overflow-hidden mb-4 cursor-pointer"
                          onClick={() => setSelectedProgram(program)}
                        >
                          <img
                            src={thumb}
                            alt={program.title}
                            loading="lazy"
                            className="w-full max-h-[260px] object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                          />
                        </div>
                      )}

                      <p className="text-sm font-bold text-gray-200 mb-1">
                        {s.date}, {s.time}
                        {e.time && <span className="font-normal text-gray-400"> – {e.time}</span>}
                      </p>

                      {program.scheduleType === 'recurring' && (
                        <p className="text-xs text-purple-400 italic mb-2 flex items-center gap-1">
                          <Repeat className="size-3" />
                          {program.recurrence?.frequency === 'daily' ? 'Daily' : 'Weekly'}
                        </p>
                      )}

                      {program.description && (
                        <p className="text-sm text-gray-300 leading-relaxed mt-1 whitespace-pre-line">
                          {program.description}
                        </p>
                      )}

                      <button
                        onClick={() => setSelectedProgram(program)}
                        className="mt-2 text-sm text-blue-400 hover:text-blue-300 font-semibold transition-colors"
                      >
                        …Detail »
                      </button>

                      <div className="border-t border-gray-700/60 mt-6" />
                    </article>
                  );
                })}
              </div>
            </>
          )}

          {/* Infinite scroll sentinel */}
          <div ref={sentinelRef} />
          {isFetching && rawPrograms.length > 0 && (
            <div className="flex justify-center py-4">
              <Loader2 className="size-5 animate-spin text-gray-500" />
            </div>
          )}
        </div>

        {/* ── RIGHT: Upcoming Programs sidebar (≈40%) ── */}
        <aside className="lg:col-span-2">
          <div>
            <h3 className="text-lg font-bold text-white mb-5 pb-2 border-b-2 border-blue-500">
              Upcoming Programs
            </h3>

            {upcomingPrograms.length === 0 ? (
              <p className="text-sm text-gray-500">No upcoming programs scheduled.</p>
            ) : (
              <div className="divide-y divide-gray-700/60">
                {upcomingPrograms.map((program) => {
                  const s = formatDt(program.startDateTime);
                  const e = formatDt(program.endDateTime);
                  const snippet = program.description ? truncate(program.description, 90) : null;

                  return (
                    <div
                      key={program.id}
                      className="py-5 first:pt-0 hover:bg-gray-800/40 -mx-2 px-2 rounded transition-colors"
                    >
                      <h4 className="text-base font-bold text-white leading-snug mb-1">
                        {program.title}
                      </h4>
                      <p className="text-xs font-semibold text-gray-300 mb-0.5">
                        {s.date}, {s.time}
                        {e.time && <span className="font-normal text-gray-500"> – {e.time}</span>}
                      </p>

                      {program.scheduleType === 'recurring' && (
                        <p className="text-[11px] text-purple-400 italic mb-1">
                          {program.recurrence?.frequency === 'daily' ? 'Daily' : 'Weekly'}
                          {program.recurrence?.frequency === 'weekly' && program.recurrence.daysOfWeek && program.recurrence.daysOfWeek.length > 0 &&
                            ` · ${program.recurrence.daysOfWeek.slice().sort((a, b) => a - b).map(d => ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d]).join(', ')}`}
                        </p>
                      )}

                      {snippet && (
                        <p className="text-sm text-gray-400 leading-relaxed mt-1">{snippet}</p>
                      )}

                      <button
                        onClick={() => setSelectedProgram(program)}
                        className="mt-1.5 text-sm text-blue-400 hover:text-blue-300 font-semibold transition-colors"
                      >
                        …Read More »
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
